import { useEffect, useState } from 'react';
import clsx from 'clsx';
import type { ToolbarTarget } from './keyTips';
import styles from './KeyTipsOverlay.module.scss';

export interface KeyTipsOverlayProps {
    open: boolean;
    targets: ToolbarTarget[];
    announce: string;
}

interface Badge {
    code: string;
    top: number;
    left: number;
    /** A nested menu-option row keeps its unchanged top-left, in-row
     *  placement — every other kind is centred over its control. */
    centered: boolean;
}

// Half the widest badge's own rendered size, rounded up — a 3-character code
// like `FDF`/`FDS`/`OI`/`OE` at `.badge`'s 0.6875rem (11px) font plus its
// 0.0625rem/0.25rem (1px/4px) padding renders to roughly 26x17px, so half of
// that (with a little headroom) is 16x10. Clamping the CENTRE point this far
// from each viewport edge keeps a `translate(-50%, -50%)`-centred badge from
// ever clipping off-screen without needing to measure the actual rendered
// node — a real font/padding change would need this re-estimated too.
const BADGE_HALF_WIDTH_PX = 16;
const BADGE_HALF_HEIGHT_PX = 10;

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

// Excel-style: every visible control's badge CENTRE sits at its own bottom
// edge, horizontally centred (`transform: translate(-50%, -50%)` in the SCSS
// resolves the rest) — straddling the control so it reads as sitting OVER it,
// clamped so it never runs off the viewport. A dense, gap-less vertical list
// (a combo popup's own options, e.g. the number-format dropdown) has no room
// below each row for this straddle — `rect.top` instead keeps a menu-option
// badge inside its OWN row, in its current (unchanged) in-row placement.
const measure = (targets: ToolbarTarget[]): Badge[] =>
    targets
        .filter((t) => t.kind !== 'overflow') // hidden inside "More" — no control to point at yet
        .map((t) => {
            const rect = t.el.getBoundingClientRect();
            if (t.kind === 'menu-option') return { code: t.code, top: rect.top, left: rect.left, centered: false };
            return {
                code: t.code,
                top: clamp(rect.bottom, BADGE_HALF_HEIGHT_PX, window.innerHeight - BADGE_HALF_HEIGHT_PX),
                left: clamp(rect.left + rect.width / 2, BADGE_HALF_WIDTH_PX, window.innerWidth - BADGE_HALF_WIDTH_PX),
                centered: true,
            };
        });

/**
 * Renders one `aria-hidden` badge per active KeyTip target, `position: fixed`
 * from each target element's own `getBoundingClientRect()` (recomputed on
 * open/nested-level change, window resize, and scroll), plus a visually
 * hidden `aria-live="polite"` announcer. Portaled as a sibling of
 * `BottomDrawer` in `SheetEditorDrawer.tsx`, matching the app dialogs' own
 * "sibling after `</BottomDrawer>`" precedent — never into Fortune's own tree.
 *
 * The live region stays mounted at all times (badges only while `open`) —
 * screen readers only announce a LIVE region's text change while it is
 * already present in the DOM, so unmounting it on close (a prior behaviour)
 * silently dropped `useKeyTips`' own "Toolbar shortcuts off." announcement.
 */
const KeyTipsOverlay: React.FC<KeyTipsOverlayProps> = ({ open, targets, announce }) => {
    const [badges, setBadges] = useState<Badge[]>([]);

    useEffect(() => {
        if (!open) {
            setBadges([]);
            return undefined;
        }
        const recompute = () => setBadges(measure(targets));
        recompute();
        // Momentum/continuous scroll can fire dozens of times a second —
        // coalesce to at most one recompute per animation frame instead of
        // one per event.
        let scheduled = false;
        const scheduleRecompute = () => {
            if (scheduled) return;
            scheduled = true;
            requestAnimationFrame(() => {
                scheduled = false;
                recompute();
            });
        };
        window.addEventListener('resize', scheduleRecompute);
        // Capture phase: a nested scrollable ancestor (the grid, a drawer
        // body) dispatches `scroll` on itself, which doesn't bubble — a
        // capture-phase listener on `window` still sees it regardless of
        // where in the tree it fired.
        window.addEventListener('scroll', scheduleRecompute, true);
        return () => {
            window.removeEventListener('resize', scheduleRecompute);
            window.removeEventListener('scroll', scheduleRecompute, true);
        };
    }, [open, targets]);

    return (
        <>
            <div className={styles.LiveRegion} aria-live="polite">
                {announce}
            </div>
            {badges.map((b) => (
                <div
                    key={b.code}
                    className={clsx(styles.Badge, b.centered && styles.Centered)}
                    style={{ top: b.top, left: b.left }}
                    aria-hidden="true"
                >
                    {b.code}
                </div>
            ))}
        </>
    );
};

export default KeyTipsOverlay;
